/**
 * Which clearance intervals get a page in the per-station PDF (spec §3.1),
 * and which intervals the summary's "worst intervals" table lists (spec §2,
 * page 2).
 *
 * ONE copy, shared by the dialog (which counts pages before it sends and
 * refuses an over-cap request with a toast) and by preprocess (which charges
 * the same count to the request-wide `requestPages` cap). Two copies of a page
 * count is how a client promises 380 pages and a server refuses 410.
 */
import { k2Verdict } from './chart';
import type { ClearanceBin } from './types';
import type { Verdict } from './verdict';

/**
 * Printed pages per request. The same figure as `MAX_STATION_PAGES` in
 * `app/src/lib/stationVolumes/perStationPdf.ts` and
 * `server/background/src/functions/preprocess.ts` — one cap for every
 * per-station document, charged to one accumulator.
 */
export const MAX_CLEARANCE_PAGES = 400;

/** `ALL` prints every scored interval; `FLAGGED` only the FAIL / MARGINAL ones. */
export type ClearancePageFilter = 'ALL' | 'FLAGGED';

/**
 * A scored interval the K2 ladder escalated (FAIL or MARGINAL). Scored first:
 * an interval the sweep scored nothing in has no section to draw, and
 * `k2Verdict` would call its zeros "clear" anyway.
 */
export const isFlaggedBin = (b: ClearanceBin): boolean => b.pointCount > 0 && k2Verdict(b) !== 'clear';

/**
 * Bins that lie WHOLLY inside `[stationFrom, stationTo]` (internal stations;
 * an absent bound is open on that side). A half-covered bin is excluded. The
 * same whole-bin rule, and the same 1e-6 tolerance, as preprocess's
 * `binsInWindow` — no new window semantics for clearance.
 */
export function binsInStationWindow<B extends { staStartInternalM: number; staEndInternalM: number }>(
  bins: readonly B[],
  stationFrom: number | null | undefined,
  stationTo: number | null | undefined,
): B[] {
  const lo = typeof stationFrom === 'number' ? stationFrom : -Infinity;
  const hi = typeof stationTo === 'number' ? stationTo : Infinity;
  return bins.filter((b) => b.staStartInternalM >= lo - 1e-6 && b.staEndInternalM <= hi + 1e-6);
}

/**
 * Split a window's bins into the ones that get a page and the two reasons a
 * bin does not: it scored nothing (`pointCount === 0` — printing an empty grid
 * would read as "clear" about ground nobody measured), or the `FLAGGED` filter
 * left out a clear one. Station order is kept in all three lists.
 */
export function clearancePageableBins(
  bins: readonly ClearanceBin[],
  filter: ClearancePageFilter,
): { pages: ClearanceBin[]; skippedUnscored: ClearanceBin[]; skippedFiltered: ClearanceBin[] } {
  const pages: ClearanceBin[] = [];
  const skippedUnscored: ClearanceBin[] = [];
  const skippedFiltered: ClearanceBin[] = [];
  for (const b of bins) {
    if (!(b.pointCount > 0)) skippedUnscored.push(b);
    else if (filter === 'FLAGGED' && !isFlaggedBin(b)) skippedFiltered.push(b);
    else pages.push(b);
  }
  return { pages, skippedUnscored, skippedFiltered };
}

/** One appendix line: a run of consecutive unprinted intervals. */
export interface ClearanceSkippedRange {
  fromDisplayM: number;
  toDisplayM: number;
  count: number;
  reason: 'unscored' | 'pass';
}

/**
 * The appendix's "intervals without a page", as MERGED station ranges rather
 * than one row per bin, so a 2000-bin run's appendix stays one page.
 *
 * Walks the window in station order. A bin not in `printed` (a set of bin
 * indices, `ClearanceBin.i`) is `unscored` when it scored nothing, otherwise
 * `pass` (a scored bin the FLAGGED filter left out). Consecutive bins with the
 * same reason merge; a printed bin breaks a range. Printed pages plus the
 * ranges' counts always add up to the window's interval count (ruling A8).
 */
export function mergeSkippedRanges(
  windowBins: readonly ClearanceBin[],
  printed: ReadonlySet<number>,
): ClearanceSkippedRange[] {
  const out: ClearanceSkippedRange[] = [];
  let open: ClearanceSkippedRange | null = null;
  for (const b of windowBins) {
    if (printed.has(b.i)) {
      open = null;
      continue;
    }
    const reason = !(b.pointCount > 0) ? 'unscored' : 'pass';
    if (open && open.reason === reason) {
      open.toDisplayM = b.staEndDisplayM;
      open.count++;
    } else {
      open = { fromDisplayM: b.staStartDisplayM, toDisplayM: b.staEndDisplayM, count: 1, reason };
      out.push(open);
    }
  }
  return out;
}

/**
 * `FLAGGED` when the run is FAIL or MARGINAL AND the window holds at least one
 * flagged interval; otherwise `ALL`. The second condition is Review Focus 3: a
 * brush over the clean stretch of a FAIL run has nothing to flag, and
 * defaulting to FLAGGED there would ask for a document of zero pages.
 */
export function defaultClearancePageFilter(runVerdict: Verdict, flaggedCount: number): ClearancePageFilter {
  return (runVerdict === 'FAIL' || runVerdict === 'MARGINAL') && flaggedCount > 0 ? 'FLAGGED' : 'ALL';
}

export interface ClearancePerStationScope {
  /** Internal stations of the window; null = the whole run. */
  stationFrom: number | null;
  stationTo: number | null;
  /** The same window in DISPLAY stations, for the label; null = the whole run. */
  displayFrom: number | null;
  displayTo: number | null;
  /** Bins in the window, unscored included. */
  windowCount: number;
  /** Pages under `ALL` (scored bins in the window). */
  allCount: number;
  /** Pages under `FLAGGED`. */
  flaggedCount: number;
  defaultFilter: ClearancePageFilter;
  /**
   * Why no option can be sent: `empty` when nothing in the window was scored,
   * `tooMany` when even the smaller non-empty option is over the cap.
   */
  block: 'empty' | 'tooMany' | null;
}

/**
 * The per-station document's scope from the dock brush — the clearance twin
 * of `perStationPdfScope` (`app/src/lib/stationVolumes/perStationPdf.ts`).
 *
 * The selection is clamped to the bin array. A brush left behind by a longer
 * run can clamp to nothing; an empty document is not what the user asked for,
 * so the request widens to the whole run (null stations), the same fallback
 * station volumes makes.
 */
export function clearancePerStationScope(
  bins: readonly ClearanceBin[],
  selection: { fromBin: number; toBin: number } | null,
  runVerdict: Verdict,
): ClearancePerStationScope {
  const lo = selection ? Math.max(0, Math.min(selection.fromBin, selection.toBin)) : 0;
  const hi = selection ? Math.min(bins.length - 1, Math.max(selection.fromBin, selection.toBin)) : bins.length - 1;
  const windowed = selection !== null && bins.length > 0 && lo <= hi;
  const range = windowed ? bins.slice(lo, hi + 1) : bins;
  const first = windowed ? range[0] : undefined;
  const last = windowed ? range[range.length - 1] : undefined;
  let allCount = 0;
  let flaggedCount = 0;
  for (const b of range) {
    if (b.pointCount > 0) allCount++;
    if (isFlaggedBin(b)) flaggedCount++;
  }
  const smallest = flaggedCount || allCount;
  return {
    stationFrom: first ? first.staStartInternalM : null,
    stationTo: last ? last.staEndInternalM : null,
    displayFrom: first ? first.staStartDisplayM : null,
    displayTo: last ? last.staEndDisplayM : null,
    windowCount: range.length,
    allCount,
    flaggedCount,
    defaultFilter: defaultClearancePageFilter(runVerdict, flaggedCount),
    block: allCount === 0 ? 'empty' : smallest > MAX_CLEARANCE_PAGES ? 'tooMany' : null,
  };
}

const desc = (a: number | null, b: number | null): number => {
  if (a == null || b == null) return a == null ? (b == null ? 0 : 1) : -1;
  return b - a;
};

/**
 * The summary's "worst intervals" table (spec §2, page 2): the top `n`
 * flagged intervals (`k2Verdict !== 'clear'`, so a PASS run returns `[]` and
 * the report prints its one sentence instead).
 *
 * With any violation in the run: deepest `maxIntrusionM` first (null last),
 * then most violations. With none: the near-miss ladder, innermost rung first
 * (`within0_1M`, then `within0_2M`, then `within0_35M`). Ties fall through to
 * the other figures and finally to station order, so the same run always
 * prints the same table.
 */
export function worstClearanceBins(bins: readonly ClearanceBin[], n = 15): ClearanceBin[] {
  const flagged = bins.filter((b) => k2Verdict(b) !== 'clear');
  const anyViolation = flagged.some((b) => b.violationCount > 0);
  const byLadder = (a: ClearanceBin, b: ClearanceBin) =>
    b.nearMiss.within0_1M - a.nearMiss.within0_1M ||
    b.nearMiss.within0_2M - a.nearMiss.within0_2M ||
    b.nearMiss.within0_35M - a.nearMiss.within0_35M;
  return flagged
    .sort((a, b) =>
      anyViolation
        ? desc(a.maxIntrusionM, b.maxIntrusionM) || b.violationCount - a.violationCount || byLadder(a, b) || a.i - b.i
        : byLadder(a, b) || a.i - b.i,
    )
    .slice(0, Math.max(0, n));
}
